// SectionTabs (F-185): a record's sections under its header. Route mode (`hrefFor` or each item's href) draws the
// design system's route tabs (#57 Tabs route); step mode (`onSelect`) draws the record's pages as numbered steps — the
// scorecard's *Respect · Ownership · Skills · Recommendation* — each marked done when nothing is open on it, the
// current one aria-current="step", with the host's position words (*Page n of N*) and status pill at the end.

import type { ReactNode } from "react";
import { Icon } from "../core/icons";
import { Tabs, type TabsItem } from "../g4a/Tabs";
import { cx } from "../lib/cx";

export interface SectionStep {
  key: string;
  label: string;
  done?: boolean | undefined;
  /** The element the step shows (aria-controls). */
  controls?: string | undefined;
}

export type SectionTabsProps =
  | { label: string; items: readonly TabsItem[]; active: string; steps?: undefined; className?: string | undefined }
  | {
    label: string; steps: readonly SectionStep[]; active: number; onSelect(index: number): void;
    /** At the end of the row: the position words and the status pill. */
    aside?: ReactNode | undefined; items?: undefined; className?: string | undefined;
  };

export function SectionTabs(props: SectionTabsProps) {
  if (!props.steps) return <Tabs route label={props.label} items={props.items} active={props.active} className={cx("mc-st", props.className)} />;
  const { label, steps, active, onSelect, aside, className } = props;
  return (
    <nav className={cx("mc-st mc-st-steps", className)} aria-label={label}>
      <ol className="mc-st-list">
        {steps.map((step, at) => (
          <li key={step.key}>
            <button
              type="button"
              className={cx("mc-st-step", step.done && "mc-st-done", at === active && "mc-on")}
              aria-controls={step.controls}
              aria-current={at === active ? "step" : undefined}
              onClick={() => onSelect(at)}
            >
              <span className="mc-st-n" aria-hidden="true">{step.done ? <Icon name="check" size={14} /> : at + 1}</span>
              {step.label}
            </button>
          </li>
        ))}
      </ol>
      {aside ? <span className="mc-st-aside">{aside}</span> : null}
    </nav>
  );
}
